import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import NextLink, { type LinkProps as NextLinkProps } from 'next/link';
import { cn } from '../../lib/utils';
import * as Headless from '@headlessui/react';

const linkVariants = cva('', {
  variants: {
    variant: {
      default:
        'text-primary hover:text-primary-dark dark:text-primary dark:hover:text-primary-light',
      subtle:
        'text-muted hover:text-foreground dark:text-muted dark:hover:text-foreground',
      underline:
        'underline underline-offset-4 hover:text-accent-dark dark:hover:text-accent-light',
      muted:
        'text-muted-foreground hover:text-foreground transition-colors',
      nav: 'text-foreground/60 hover:text-foreground/80 transition-colors',
      primary: 'text-primary hover:text-primary/80 transition-colors',
      secondary:
        'text-secondary hover:text-secondary/80 dark:text-secondary dark:hover:text-secondary/90 transition-colors',
      accent: 'text-accent hover:text-accent/80 transition-colors',
      destructive:
        'text-destructive hover:text-destructive/80 transition-colors',
      // Color variants
      crimson: 'text-flame hover:text-flame/80 transition-colors',
      mint: 'text-sunlight-dark hover:text-sunlight/80 transition-colors dark:text-sunlight dark:hover:text-sunlight/90',
      blue: 'text-ocean hover:text-ocean/80 transition-colors',
      black:
        'text-black hover:text-black/80 transition-colors dark:text-white dark:hover:text-white/90',
      // Additional button variants
      outline:
        'text-foreground hover:text-accent-foreground transition-colors',
      ghost:
        'text-foreground hover:text-accent-foreground transition-colors',
      link: 'text-primary underline-offset-4 hover:underline hover:text-primary/80',
      plain: 'text-foreground hover:text-accent transition-colors',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface LinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>,
    NextLinkProps,
    VariantProps<typeof linkVariants> {
  useOwnVariants?: boolean;
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  function Link(
    {
      className,
      variant,
      useOwnVariants = true,
      ...props
    }: LinkProps,
    ref
  ) {
    const classes = useOwnVariants
      ? cn(linkVariants({ variant }), className)
      : className;
    return (
      <Headless.DataInteractive>
        <NextLink className={classes} {...props} ref={ref} />
      </Headless.DataInteractive>
    );
  }
);

export { linkVariants };
